import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, User, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const NAV = [
  { to: "/", label: "Inicio" },
  { to: "/sorteos-exclusivos", label: "Sorteos Exclusivos" },
  { to: "/acciones-de-caza", label: "Acciones de Caza" },
  { to: "/homologa-tu-trofeo", label: "Homologa Tu Trofeo" },
  { to: "/video-club", label: "Video Club" },
  { to: "/fincas", label: "Nuestras Fincas" },
  { to: "/modalidades", label: "Modalidades" },
  { to: "/tienda", label: "Tienda" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  const linkCls = ({ isActive }) =>
    `text-[11px] uppercase tracking-[0.15em] font-semibold transition-colors duration-200 ${
      isActive ? "text-gold" : "text-white/70 hover:text-white"
    }`;

  const renderNav = (onClick) =>
    NAV.map((item) =>
      item.external ? (
        <a
          key={item.label}
          href={item.to}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          data-testid="nav-fincas-link"
          className="text-[11px] uppercase tracking-[0.15em] font-semibold text-white/70 hover:text-white transition-colors duration-200"
        >
          {item.label}
        </a>
      ) : (
        <NavLink key={item.to} to={item.to} onClick={onClick} className={linkCls} data-testid={`nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}>
          {item.label}
        </NavLink>
      )
    );

  return (
    <header className="sticky top-0 z-50 bg-carbon/80 backdrop-blur-xl border-b border-white/10" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex flex-col items-start gap-0" data-testid="logo-link">
            <span className="font-heading text-xl lg:text-2xl font-semibold tracking-wide text-white leading-none">
              CINEGÉTICA <span className="text-gold">RUTA DEL AGUA</span>
            </span>
            <span className="text-xs lg:text-sm uppercase tracking-[0.25em] text-sand mt-1">
              Club de Caza · Guillena
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6" data-testid="desktop-nav">
            {renderNav()}
            {user && user.role === "admin" && (
              <NavLink to="/gestion-tienda" className={linkCls} data-testid="nav-gestion-admin">
                Gestión Tienda
              </NavLink>
            )}
          </nav>

          <div className="hidden xl:flex items-center gap-4">
            <Link to="/checkout" className="relative text-white/70 hover:text-gold transition-colors duration-200" data-testid="cart-link">
              <ShoppingBag size={20} strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <div className="flex items-center gap-4">
                {user.role === "admin" && (
                  <Link to="/admin" className="text-white/70 hover:text-gold transition-colors duration-200" data-testid="admin-link" title="Panel Admin">
                    <ShieldCheck size={20} strokeWidth={1.5} />
                  </Link>
                )}
                <Link to="/area-socio" className="text-white/70 hover:text-gold transition-colors duration-200" data-testid="profile-link" title="Área de Socio">
                  <User size={20} strokeWidth={1.5} />
                </Link>
                <button onClick={async () => { await logout(); navigate("/"); }} className="text-white/50 hover:text-gold transition-colors duration-200" data-testid="logout-button" title="Cerrar sesión">
                  <LogOut size={18} strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <Link to="/login" className="text-white/70 hover:text-gold transition-colors duration-200" data-testid="login-link" title="Acceder">
                <User size={20} strokeWidth={1.5} />
              </Link>
            )}
            <Link to="/hazte-socio" className="btn-gold !px-6 !py-2.5" data-testid="cta-hazte-socio">
              Hazte Socio
            </Link>
          </div>

          <div className="flex xl:hidden items-center gap-5">
            <Link to="/checkout" className="relative text-white/80" data-testid="cart-link-mobile">
              <ShoppingBag size={22} strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            <button onClick={() => setOpen(!open)} className="text-white" data-testid="mobile-menu-button" aria-label="Menú">
              {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-white/10 bg-carbon/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-5">
            {renderNav(() => setOpen(false))}
            <div className="flex items-center gap-5 pt-2 border-t border-white/10">
              {user ? (
                <>
                  <Link to="/area-socio" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-[0.15em] font-semibold text-white/70" data-testid="profile-link-mobile">
                    Mi Área
                  </Link>
                  {user.role === "admin" && (
                    <>
                      <Link to="/admin" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-[0.15em] font-semibold text-white/70" data-testid="admin-link-mobile">
                        Admin
                      </Link>
                      <Link to="/gestion-tienda" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-[0.15em] font-semibold text-gold" data-testid="gestion-link-mobile">
                        Gestión Tienda
                      </Link>
                    </>
                  )}
                  <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-[11px] uppercase tracking-[0.15em] font-semibold text-white/50" data-testid="logout-button-mobile">
                    Salir
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-[0.15em] font-semibold text-white/70" data-testid="login-link-mobile">
                  Acceder
                </Link>
              )}
            </div>
            <Link to="/hazte-socio" onClick={() => setOpen(false)} className="btn-gold-solid justify-center" data-testid="cta-hazte-socio-mobile">
              Hazte Socio
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
